import { Injectable, isDevMode } from '@angular/core';
import { EcommerceEvent, EcommerceEventParameters } from './analytics.models';
import { GA4EventNameEnum, GA4ParameterNameEnum, UserTypeEnum } from './analytics.constants';

/**
 * First-party analytics seam — privacy-first, zero third-party trackers (ADR-0035).
 *
 * This service is the ONLY analytics entry point the Angular portals may call.
 * It deliberately has **no network sink**: nothing leaves the browser, no
 * cookie or identifier is set, and no third-party script (Google Analytics,
 * gtag, GTM, …) is loaded anywhere in the portals. Product metrics are computed
 * server-side from data the platform already holds (bookings, check-ins,
 * completed tests — see `BusinessMetricsService` in `@bodyanalytics/common-api`).
 *
 * Why keep a service at all? The call sites (page views, form submits, button
 * clicks) are the instrumentation seam. If a first-party, self-hosted sink is
 * ever adopted, it plugs in here — behind a recorded consent decision — instead
 * of re-instrumenting every component. Until then every method is a no-op in
 * production and a `console.debug` line in dev builds.
 *
 * Rules for callers (enforced by review, not by type):
 *  - never put a customer / booking / order / event id in an event name or
 *    page path — use the id-less route segment (`'signup-verify'`, not
 *    `'signup-verify/' + id`);
 *  - never pass measurements, names, emails or phone numbers as parameters.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly isDevelopmentMode = isDevMode();
  private currentUserId: string | null = null;
  private currentUserType: string = UserTypeEnum.ANONYMOUS;

  /**
   * Remember the signed-in principal for parameter enrichment.
   * Kept in memory only — never persisted, never transmitted.
   * @param userId - User ID from Auth0 or null to clear
   */
  public setUserId(userId: string | null): void {
    this.currentUserId = userId;
    this.logDebug(`User ID ${userId ? 'set' : 'cleared'}`);
  }

  /**
   * Set user type for automatic enrichment
   * @param userType - Type of user (admin, operator, customer, anonymous)
   */
  public setUserType(userType: string): void {
    this.currentUserType = userType;
    this.logDebug(`User type set: ${userType}`);
  }

  /**
   * Record a page view. No-op outside dev builds.
   * @param pagePath - Id-less path of the page being viewed
   * @param pageTitle - Title of the page
   * @param customParameters - Additional custom parameters
   */
  public trackPageView(
    pagePath: string,
    pageTitle: string,
    customParameters?: Record<string, string | number | boolean>,
  ): void {
    const parameters = this.enrich(customParameters);
    this.logDebug('Page view:', { pagePath, pageTitle, parameters });
  }

  /**
   * Record a custom event. No-op outside dev builds.
   * @param eventName - Name of the event
   * @param parameters - Event parameters
   */
  public trackEvent(eventName: string, parameters?: Record<string, string | number | boolean>): void {
    this.logDebug('Event:', { eventName, parameters: this.enrich(parameters) });
  }

  /**
   * Record an e-commerce event. No-op outside dev builds.
   * @param event - E-commerce event details
   */
  public trackEcommerceEvent(event: EcommerceEvent): void {
    const parameters: EcommerceEventParameters = {
      ...event.parameters,
      [GA4ParameterNameEnum.USER_TYPE]: event.parameters[GA4ParameterNameEnum.USER_TYPE] || this.currentUserType,
      [GA4ParameterNameEnum.TIMESTAMP]: new Date().toISOString(),
    };
    this.logDebug('E-commerce event:', { eventName: event.eventName, parameters });
  }

  /**
   * Track button click
   * @param buttonName - Name/ID of the button clicked
   * @param additionalParams - Additional parameters
   */
  public trackButtonClick(buttonName: string, additionalParams?: Record<string, string | number | boolean>): void {
    this.trackEvent(GA4EventNameEnum.BUTTON_CLICK, {
      event_label: buttonName,
      ...additionalParams,
    });
  }

  /**
   * Track form submission
   * @param formName - Name/ID of the form
   * @param additionalParams - Additional parameters
   */
  public trackFormSubmit(formName: string, additionalParams?: Record<string, string | number | boolean>): void {
    this.trackEvent(GA4EventNameEnum.FORM_SUBMIT, {
      event_label: formName,
      ...additionalParams,
    });
  }

  /**
   * Get current user ID
   */
  public getUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Get current user type
   */
  public getUserType(): string {
    return this.currentUserType;
  }

  private enrich(parameters?: Record<string, string | number | boolean>): Record<string, string | number | boolean> {
    return {
      ...parameters,
      [GA4ParameterNameEnum.USER_TYPE]: parameters?.[GA4ParameterNameEnum.USER_TYPE] || this.currentUserType,
      [GA4ParameterNameEnum.TIMESTAMP]: new Date().toISOString(),
    };
  }

  /** Dev-build-only breadcrumb; silent in production bundles. */
  private logDebug(message: string, data?: unknown): void {
    if (!this.isDevelopmentMode) {
      return;
    }
    try {
      if (data) {
        console.debug(`[Analytics] ${message}`, data);
      } else {
        console.debug(`[Analytics] ${message}`);
      }
    } catch {
      // Analytics must never break a user flow.
    }
  }
}
